import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Card, InputGroup, Badge, Spinner, Alert } from 'react-bootstrap';
import { createTask, Task, TaskType, JobEmploymentType } from '../../services/taskService';
// TODO: Import useAuth from AuthContext to get user location
// import { useAuth } from '../../contexts/AuthContext';

interface TaskCreateFormProps {
  onTaskCreated: (newTask: Task) => void;
  onCancel: () => void;
}

const TaskCreateForm: React.FC<TaskCreateFormProps> = ({ onTaskCreated, onCancel }) => {
  // Form State
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<TaskType | '' >('');
  const [description, setDescription] = useState('');
  const [skills, setSkills] = useState<string[]>([]);
  const [skillInput, setSkillInput] = useState('');
  const [isRemote, setIsRemote] = useState(false);
  // Location - TODO: Populate from user context
  const [locationName, setLocationName] = useState('Fetching location...');
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  // Task Specific
  const [deadline, setDeadline] = useState(''); // Use string for input type='datetime-local'
  const [estimatedHours, setEstimatedHours] = useState<number | '' >('');
  // Job Specific
  const [employmentType, setEmploymentType] = useState<JobEmploymentType | '' >('');
  const [duration, setDuration] = useState('');
  const [compensation, setCompensation] = useState('');

  // Submission State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // TODO: Fetch user location from context
  // const { user } = useAuth();
  useEffect(() => {
    // Placeholder for fetching user location
    // if (user && user.latitude && user.longitude) {
    //   setLatitude(user.latitude);
    //   setLongitude(user.longitude);
    //   setLocationName(`${user.city || 'Unknown City'}, ${user.country || 'Unknown Country'}`);
    // } else {
       setLocationName('Location not set in profile');
    // }
  }, [/* user */]); // Dependency on user from context

  const handleAddSkill = () => {
    const trimmedSkill = skillInput.trim();
    if (trimmedSkill && !skills.includes(trimmedSkill)) {
      setSkills([...skills, trimmedSkill]);
      setSkillInput('');
    }
  };

  const handleRemoveSkill = (skillToRemove: string) => {
    setSkills(skills.filter(skill => skill !== skillToRemove));
  };

  const handleSkillInputKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent form submission on Enter key in skill input
      handleAddSkill();
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(null);
    setIsSubmitting(true);

    // Basic Validation
    if (!title || !description || !category) {
      setSubmitError('Please fill in Title, Description, and Category.');
      setIsSubmitting(false);
      return;
    }

    // Prepare data for API, explicitly setting default status
    const taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt' | 'ownerId' | 'owner'> = {
        title,
        description,
        type: category,
        status: 'OPEN', // Explicitly set default status for creation
        skills,
        isRemote,
        locationName: !isRemote ? locationName : null, // Only send if not remote
        latitude: !isRemote ? latitude : null,
        longitude: !isRemote ? longitude : null,
        // Task Specific
        deadline: category === 'TASK' && deadline ? new Date(deadline).toISOString() : null,
        estimatedHours: category === 'TASK' && estimatedHours ? Number(estimatedHours) : null,
        // Job Specific
        employmentType: category === 'JOB' && employmentType ? employmentType : null,
        duration: category === 'JOB' ? duration : null,
        compensation: category === 'JOB' ? compensation : null,
    };

    try {
      const newTask = await createTask(taskData);
      onTaskCreated(newTask); // Call the callback with the new task data
    } catch (error: any) {
      console.error("Error creating task:", error);
      setSubmitError(error.message || 'Failed to create task. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="mb-4 border shadow-sm"> 
      <Card.Body>
        <h4 className="mb-3">Create New Task/Job</h4>
        {submitError && <Alert variant="danger">{submitError}</Alert>}
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={8}>
              <Form.Group className="mb-3" controlId="taskTitle">
                <Form.Label>Title</Form.Label>
                <Form.Control 
                    type="text" 
                    placeholder="e.g., Analyze Sales Data for Q3" 
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3" controlId="taskCategory">
                <Form.Label>Category</Form.Label>
                <Form.Select 
                    value={category}
                    onChange={(e) => setCategory(e.target.value as TaskType | '')}
                    required
                >
                  <option value="" disabled>Select...</option>
                  <option value="TASK">Task (Contract/Freelance)</option>
                  <option value="JOB">Job (Formal Employment)</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3" controlId="taskDescription">
            <Form.Label>Description</Form.Label>
            <Form.Control 
                as="textarea" 
                rows={3} 
                placeholder="Describe the task or job requirements in detail..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required 
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="taskSkills">
            <Form.Label>Required Skills</Form.Label>
            <div className="mb-2">
              {skills.map(skill => (
                <Badge pill bg="primary" key={skill} className="me-2 mb-1 p-2" style={{ cursor: 'pointer' }} onClick={() => handleRemoveSkill(skill)} title={`Remove ${skill}`}>
                  {skill} &times;
                </Badge>
              ))}
              {skills.length === 0 && <small className="text-muted">No skills added yet.</small>}
            </div>
            <InputGroup>
              <Form.Control 
                type="text" 
                placeholder="Add a skill (e.g., Python, SQL)" 
                value={skillInput}
                onChange={(e) => setSkillInput(e.target.value)}
                onKeyPress={handleSkillInputKeyPress}
              />
              <Button variant="outline-secondary" onClick={handleAddSkill}>Add</Button>
            </InputGroup>
            <Form.Text>Press Enter or click Add after typing a skill.</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3" controlId="isRemote">
            <Form.Check 
              type="checkbox"
              label="This is a remote task/job"
              checked={isRemote}
              onChange={(e) => setIsRemote(e.target.checked)}
            />
          </Form.Group>

          {!isRemote && (
            <Form.Group className="mb-3" controlId="locationName">
              <Form.Label>Location</Form.Label>
              <Form.Control 
                type="text" 
                value={locationName} 
                readOnly 
                disabled 
              />
               <Form.Text className="text-muted">
                Based on your profile location. 
                {/* TODO: Add link to profile settings */} 
              </Form.Text>
            </Form.Group>
          )}

          {/* Task Specific Fields */} 
          {category === 'TASK' && (
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="taskDeadline">
                    <Form.Label>Deadline (Optional)</Form.Label>
                    <Form.Control 
                        type="datetime-local" 
                        value={deadline}
                        onChange={(e) => setDeadline(e.target.value)}
                    />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="taskEstimatedHours">
                    <Form.Label>Estimated Hours (Optional)</Form.Label>
                    <Form.Control 
                        type="number"
                        min="0"
                        placeholder="e.g., 10"
                        value={estimatedHours}
                        onChange={(e) => setEstimatedHours(e.target.value === '' ? '' : Number(e.target.value))}
                    />
                </Form.Group>
              </Col>
            </Row>
          )}

          {/* Job Specific Fields */} 
          {category === 'JOB' && (
            <Row>
                <Col md={4}>
                    <Form.Group className="mb-3" controlId="jobEmploymentType">
                        <Form.Label>Employment Type</Form.Label>
                        <Form.Select 
                            value={employmentType}
                            onChange={(e) => setEmploymentType(e.target.value as JobEmploymentType | '')}
                        >
                        <option value="">Select...</option>
                        <option value="FULL_TIME">Full-time</option>
                        <option value="PART_TIME">Part-time</option>
                        <option value="CONTRACT">Contract</option>
                        <option value="FREELANCE">Freelance</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="mb-3" controlId="jobDuration">
                        <Form.Label>Duration (Optional)</Form.Label>
                        <Form.Control 
                            type="text" 
                            placeholder="e.g., 3 months, Ongoing"
                            value={duration}
                            onChange={(e) => setDuration(e.target.value)}
                        />
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="mb-3" controlId="jobCompensation">
                        <Form.Label>Compensation (Optional)</Form.Label>
                        <Form.Control 
                            type="text" 
                            placeholder="e.g., $25/hr, $60k-$70k/year"
                            value={compensation}
                            onChange={(e) => setCompensation(e.target.value)}
                        />
                    </Form.Group>
                </Col>
            </Row>
          )}

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Creating...</> : 'Create Task/Job'}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default TaskCreateForm; 